import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Loader2, CheckCircle, FileText, Home, User, PoundSterling,
  Calendar, Pen, AlertCircle, RotateCcw, Check, Users, Clock
} from "lucide-react";
import { API } from "@/App";

// Helper function for ordinal suffixes (1st, 2nd, 3rd, etc.)
const getOrdinal = (n) => {
  const num = parseInt(n, 10);
  if (isNaN(num)) return n;
  const s = ["th", "st", "nd", "rd"];
  const v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const DocumentSignPage = () => {
  const { token } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [document, setDocument] = useState(null);
  const [signer, setSigner] = useState(null);
  const [signingProgress, setSigningProgress] = useState(null);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [isTenancy, setIsTenancy] = useState(false);
  const [isSalesInstruction, setIsSalesInstruction] = useState(false);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Agreement checkboxes
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedAccuracy, setAgreedAccuracy] = useState(false);

  useEffect(() => {
    fetchDocument();
  }, [token]);

  useEffect(() => {
    if (canvasRef.current && !signed) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [document, signed]);

  const fetchDocument = async () => {
    try {
      // Try multiple endpoints in order: sales instruction, tenancy, then LMA
      let response;
      try {
        response = await axios.get(`${API}/esignature/sales-instruction/sign/${token}`);
        if (response.data.is_sales_instruction) {
          setIsSalesInstruction(true);
        }
      } catch (e) {
        try {
          response = await axios.get(`${API}/esignature/documents/sign-tenancy/${token}`);
        } catch (e2) {
          // Fallback to standard LMA endpoint
          response = await axios.get(`${API}/esignature/documents/sign/${token}`);
        }
      }
      
      setDocument(response.data.document);
      setSigner(response.data.signer || null);
      setSigningProgress(response.data.signing_progress || null);
      setIsTenancy(response.data.is_tenancy || false);
      setIsSalesInstruction(response.data.is_sales_instruction || response.data.document?.document_type === "sales_instruction");
      
      if (response.data.already_signed || response.data.document?.status === "signed") {
        setSigned(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired signing link");
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!hasSignature) {
      toast.error("Please sign in the box above");
      return;
    }
    if (!agreedTerms || !agreedAccuracy) {
      toast.error("Please agree to the terms to continue");
      return;
    }

    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");
      
      // Use appropriate endpoint based on document type
      let endpoint;
      if (isSalesInstruction) {
        endpoint = `${API}/esignature/sales-instruction/sign/${token}`;
      } else if (isTenancy) {
        endpoint = `${API}/esignature/documents/sign-tenancy/${token}`;
      } else {
        endpoint = `${API}/esignature/documents/sign/${token}`;
      }
      
      const response = await axios.post(endpoint, {
        signature: signatureData
      });
      
      setSigned(true);
      
      // For tenancy agreements, show progress
      if (response.data.signing_progress) {
        setSigningProgress(response.data.signing_progress);
      }
      
      toast.success("Document signed successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign document");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-slate-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-700 mb-2">Unable to Load Document</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <p className="text-sm text-muted-foreground">
              Please contact Goodacres Residential if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    const isFullySigned = signingProgress?.fully_signed || signingProgress?.signed >= signingProgress?.total;
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-slate-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">
              {isTenancy ? (isFullySigned ? "Agreement Fully Signed!" : "Your Signature Received!") : "Document Signed!"}
            </h2>
            <p className="text-muted-foreground mb-4">
              Thank you for signing. A confirmation email has been sent to you.
            </p>
            
            {/* Show signing progress for tenancy agreements */}
            {isTenancy && signingProgress && !isFullySigned && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 text-left">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  Signing Progress: {signingProgress.signed} of {signingProgress.total}
                </p>
                {signingProgress.signed_parties && signingProgress.signed_parties.length > 0 && (
                  <div className="space-y-1">
                    {signingProgress.signed_parties.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-blue-700">
                        <CheckCircle className="w-3 h-3" />
                        <span>{p.name} ({p.type})</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-blue-600 mt-2">
                  Waiting for {signingProgress.remaining || (signingProgress.total - signingProgress.signed)} more signature(s)
                </p>
              </div>
            )}
            
            {isTenancy && isFullySigned && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-green-800">
                  All parties have signed! A copy of the fully executed agreement will be emailed to everyone.
                </p>
              </div>
            )}
            
            <p className="text-sm text-muted-foreground mt-4">
              You can close this window now.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLMA = document?.document_type === "lma";
  const isTenancyAgreement = document?.document_type === "tenancy_agreement";
  const isSalesContract = document?.document_type === "sales_instruction";
  const signerName = signer?.name || document?.vendor_name || document?.recipient_name || "Valued Client";
  const signerType = signer?.signer_type || document?.recipient_type;

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    return `£${parseFloat(amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  // Get document title
  const getDocumentTitle = () => {
    if (isSalesContract) return "Sales Instruction Form";
    if (isLMA) return "Landlord Management Agreement";
    if (isTenancyAgreement) return "Tenancy Agreement";
    return "Document";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Goodacres Residential Limited</h1>
          <p className="text-muted-foreground">Document Signing Portal</p>
        </div>

        {/* Document Card */}
        <Card className="mb-6" data-testid="document-card">
          <CardHeader className={`text-white rounded-t-lg ${isSalesContract ? 'bg-gradient-to-r from-slate-700 to-slate-800' : 'bg-gradient-to-r from-blue-600 to-blue-700'}`}>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {getDocumentTitle()}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Signer Info */}
            <div className="flex items-center gap-3 mb-6 pb-6 border-b">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold">{signerName}</p>
                <p className="text-sm text-muted-foreground">
                  {isSalesContract 
                    ? `Signing as Vendor` 
                    : signerType === "landlord" 
                      ? "Signing as Landlord" 
                      : signerType === "tenant" 
                        ? "Signing as Tenant" 
                        : signer?.email || document?.vendor_email || document?.recipient_email
                  }
                </p>
              </div>
              {(signerType || isSalesContract) && (
                <Badge className="ml-auto" variant={isSalesContract ? "outline" : signerType === "landlord" ? "default" : "secondary"}>
                  {isSalesContract ? "Vendor" : signerType?.charAt(0).toUpperCase() + signerType?.slice(1)}
                </Badge>
              )}
            </div>

            {/* Property Info */}
            {document?.property_address && (
              <div className="flex items-start gap-3 mb-6 pb-6 border-b">
                <Home className="w-5 h-5 text-slate-500 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Property</p>
                  <p className="font-medium">{document.property_address}</p>
                </div>
              </div>
            )}

            {/* Signing Progress for Tenancy */}
            {isTenancyAgreement && signingProgress && (
              <div className="bg-slate-50 border rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium">Signing Progress</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-600 transition-all"
                      style={{ width: `${(signingProgress.signed / signingProgress.total) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {signingProgress.signed}/{signingProgress.total}
                  </span>
                </div>
                {signingProgress.signed_parties && signingProgress.signed_parties.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {signingProgress.signed_parties.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle className="w-3 h-3" />
                        <span>{p.name} ({p.type}) - Signed</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Document Details */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {/* Sales Instruction Details */}
              {isSalesContract && (
                <>
                  {document?.asking_price && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Asking Price</p>
                      <p className="font-semibold text-lg">{formatCurrency(document.asking_price)}</p>
                    </div>
                  )}
                  {document?.commission_percent && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Commission</p>
                      <p className="font-semibold">{document.commission_percent}% + VAT</p>
                      <p className="text-xs text-muted-foreground">
                        ({formatCurrency(document.total_with_vat)} inc. VAT)
                      </p>
                    </div>
                  )}
                  {document?.agency_type && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Agency Type</p>
                      <p className="font-semibold capitalize">{document.agency_type.replace(/_/g, " ")}</p>
                    </div>
                  )}
                  {document?.agency_period_weeks && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Agency Period</p>
                      <p className="font-semibold">{document.agency_period_weeks} weeks</p>
                      <p className="text-xs text-muted-foreground">
                        Min. {document.minimum_term_weeks || 8} weeks
                      </p>
                    </div>
                  )}
                  {document?.tie_in_clause !== undefined && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Tie-In Clause</p>
                      <p className="font-semibold">{document.tie_in_clause ? "Yes" : "No"}</p>
                    </div>
                  )}
                  {document?.notice_period_weeks && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Notice Period</p>
                      <p className="font-semibold">{document.notice_period_weeks} weeks</p>
                    </div>
                  )}
                </>
              )}
              {isLMA ? (
                <>
                  {document?.service_level && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Service Level</p>
                      <p className="font-semibold capitalize">{document.service_level.replace(/_/g, " ")}</p>
                    </div>
                  )}
                  {document?.management_fee_percent && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Management Fee</p>
                      <p className="font-semibold">{document.management_fee_percent}% + VAT</p>
                    </div>
                  )}
                  {document?.setup_fee && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Setup Fee</p>
                      <p className="font-semibold">£{document.setup_fee} + VAT</p>
                    </div>
                  )}
                  {document?.monthly_rent && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Monthly Rent</p>
                      <p className="font-semibold">£{document.monthly_rent}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {document?.landlord_name && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Landlord</p>
                      <p className="font-semibold">{document.landlord_name}</p>
                    </div>
                  )}
                  {document?.tenant_names && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Tenant(s)</p>
                      <p className="font-semibold">{document.tenant_names}</p>
                    </div>
                  )}
                  {document?.rent_amount && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Monthly Rent</p>
                      <p className="font-semibold">£{document.rent_amount.toLocaleString()}</p>
                    </div>
                  )}
                  {document?.tenancy_start_date && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Start Date</p>
                      <p className="font-semibold">{document.tenancy_start_date}</p>
                    </div>
                  )}
                  {document?.tenancy_term_months && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Term</p>
                      <p className="font-semibold">{document.tenancy_term_months} months</p>
                    </div>
                  )}
                  {document?.deposit_amount && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Deposit</p>
                      <p className="font-semibold">£{document.deposit_amount.toLocaleString()}</p>
                    </div>
                  )}
                  {document?.rent_payment_day && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Rent Due</p>
                      <p className="font-semibold">{getOrdinal(document.rent_payment_day)} of each month</p>
                    </div>
                  )}
                  {document?.break_clause && (
                    <div className="bg-slate-50 p-4 rounded-lg col-span-2">
                      <p className="text-sm text-muted-foreground">Break Clause</p>
                      <p className="font-semibold">{document.break_clause}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Additional Terms for Tenancy */}
            {isTenancyAgreement && document?.additional_terms && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-amber-800 mb-1">Additional Terms</p>
                <p className="text-sm text-amber-700">{document.additional_terms}</p>
              </div>
            )}

            {/* Special Conditions for Sales Instruction */}
            {isSalesContract && document?.special_conditions && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-amber-800 mb-1">Special Conditions</p>
                <p className="text-sm text-amber-700">{document.special_conditions}</p>
              </div>
            )}

            {/* Terms Summary */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> By signing below, you agree to the terms and conditions 
                of this {isSalesContract ? "Sales Instruction Form" : isLMA ? "Landlord Management Agreement" : "Tenancy Agreement"} with 
                Goodacres Residential Limited.
                {isSalesContract && (
                  <span className="block mt-2">
                    This authorizes Goodacres Residential to market your property on your behalf at the agreed asking price 
                    and commission rate for the specified agency period.
                  </span>
                )}
              </p>
            </div>

            {/* Signature Section */}
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Pen className="w-5 h-5 text-slate-600" />
                  <span className="font-medium">Your Signature</span>
                </div>
                {hasSignature && (
                  <Button variant="ghost" size="sm" onClick={clearSignature} data-testid="clear-signature-btn">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                )}
              </div>
              
              <canvas
                ref={canvasRef}
                width={500}
                height={150}
                className="w-full border border-slate-200 rounded-lg bg-white cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="signature-canvas"
              />
              
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Sign using your mouse or finger (on touch devices)
              </p>
            </div>

            {/* Agreement Checkboxes */}
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={agreedTerms}
                  onCheckedChange={setAgreedTerms}
                  data-testid="agree-terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                  I have read and agree to the terms and conditions of this agreement
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="accuracy"
                  checked={agreedAccuracy}
                  onCheckedChange={setAgreedAccuracy}
                  data-testid="agree-accuracy-checkbox"
                />
                <Label htmlFor="accuracy" className="text-sm leading-relaxed cursor-pointer">
                  I confirm that the information provided is accurate and I am authorized to sign this document
                </Label>
              </div>
            </div>

            {/* Sign Button */}
            <Button
              onClick={handleSign}
              disabled={signing || !hasSignature || !agreedTerms || !agreedAccuracy}
              className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
              data-testid="sign-document-btn"
            >
              {signing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Signing...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Sign Document
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Goodacres Residential Limited</p>
          <p className="mt-1">This document is legally binding once signed.</p>
        </div>
      </div>
    </div>
  );
};

export default DocumentSignPage;
